import Heading from "@/components/Atoms/Heading";
import Modal from "@/components/Atoms/Modal";
import Paragraph from "@/components/Atoms/Paragraph";
import Skeleton from "@/components/Atoms/Skeleton";
import { getCities } from "@/utils/CountriesConfig";
import { useMemo, useRef, useState } from "react";
import { getCoverageZones } from "@/services/stores";
import Analytics from "@/utils/Analytics";
import NotificationAlert, { ETypeNotification } from "@/components/Atoms/NotificationAlert";
import Button from "@/components/Atoms/Button";
import CityFeedbackForm from "@/components/Molecules/CityFeedbackForm";
import AddressSelectionCard from "@/components/Molecules/AddressSelectionCard";
import IconOtra from "@iconsV2/Illustration/otra.svg?react";
import useAppSelector from "@/hooks/useAppSelector";
import { COUNTRYCODE } from "@/recourses/constants";
import tw, { theme } from "twin.macro";
import IconClose from "@iconsV2/Edit/close.svg?react";
import { popupsRemunderAction } from "@/store/PopUpsReminderSlice";
import useAppDispatch from "@/hooks/useAppDispatch";
import useCart from "@/hooks/useCartV2";
import { useQuery } from "@tanstack/react-query";
import { CheckoutEnum, StoresEnum } from "@/recourses/queryKeys";
import { queryClient } from "@/recourses/queryClient";
import { getCartAvailableProducts } from "@/services/checkout";
import { commitStoreSelection } from "@/utils/storeCoverage";
import ModalItemsOutOfStock from "../ModalItemsOutOfStock";

import {
  StyledAddresFormLoader,
  StyledLoaderCheked,
  StyledLoaderLoading,
  StyledSectionSelect,
  StyledWrapperAddresForm,
  StyledWrapperHeader,
} from "./styles";
import useModalAddress from "@/hooks/useAddressModal";
import { pageView } from "@/utils/analyticsV2";

interface ModalAddressFormProps {
  isOpen: boolean;
  toggleAddressModal: (b: boolean) => void;
}

export type FnCloseModal = (
  event?: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLDivElement, MouseEvent>
) => void;

interface ICoverageOption extends CityType {
  zone?: ICoverageZone;
}

const cities = getCities(COUNTRYCODE);

const buildCoverageOptions = (zones: ICoverageZone[]): ICoverageOption[] => {
  const zoneOptions = zones
    .filter((zone) => zone.stores.length > 0)
    .map((zone): ICoverageOption => {
      const store = zone.stores[0];
      const cityConfig = cities?.find((city) => city.code === zone.city || city.city === zone.city);
      const cityName = cityConfig?.name ?? zone.name;
      const hasSeveralZones = zones.filter((item) => item.city === zone.city).length > 1;

      return {
        name: hasSeveralZones ? `${cityName} - ${store.name_display || store.name}` : cityName,
        code: zone.city,
        city: zone.city,
        lat: store.latitude,
        lng: store.longitude,
        image: cityConfig?.image,
        lowImageQuality: cityConfig?.lowImageQuality,
        is_available: true,
        is_new: cityConfig?.is_new ?? false,
        is_National: false,
        region_id: zone.city,
        zone,
      };
    });

  // Upcoming cities stay disabled
  const upcomingCities = (cities ?? []).filter(
    (city) => city.is_new && !city.is_available && !zoneOptions.some((option) => option.code === city.code)
  );

  return [...zoneOptions, ...upcomingCities];
};

const ModalAddressForm = ({ isOpen, toggleAddressModal }: ModalAddressFormProps) => {
  const { handleCallback } = useModalAddress();
  const dispatch = useAppDispatch();
  const { totalCountCart, items } = useCart();

  const pendingOptionRef = useRef<ICoverageOption | undefined>(undefined);
  const [showCityFeedbackForm, setShowCityFeedbackForm] = useState<boolean>(false);
  const availableStore = useAppSelector((state) => state.cart.availableStores);
  const storeId = useAppSelector((state) => state.cart.storeId);
  const location = useAppSelector((state) => state.cart.location);
  const showLocationModal = useAppSelector((state) => state.popUpsReminder.showLocationModal);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [succesChange, setSuccesChange] = useState<boolean>(false);
  const [openModalItemsOutStock, setOpenModalItemsOutStock] = useState<boolean>(false);
  const [cityFeedbackForm, setCityFeedbackForm] = useState("");

  // Fresh on every open
  const {
    data: zones,
    isFetchedAfterMount,
    isError,
    refetch,
  } = useQuery({
    queryKey: [StoresEnum.getCoverageZones],
    queryFn: getCoverageZones,
    enabled: isOpen,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  const isLoadingZones = !isFetchedAfterMount;
  const listStores = useMemo(() => buildCoverageOptions(zones ?? []), [zones]);

  // Validate if exist a city selected or select the first one as default
  const cityDefault =
    listStores.find((item) => item.zone && item.city === availableStore?.city) ??
    listStores.find((item) => item.zone);

  const isOptionSelected = (option: ICoverageOption) =>
    !!option.zone && (option.zone.stores[0]?.id === storeId || option.zone.id === availableStore?.id);

  const sendCoverageChangeEvent = (
    newLocation: ICoverageOption,
    newStoreId: number,
    newCoverageId: number
  ) => {
    Analytics.CoverageChange({
      lastAddress: location?.address,
      lastCoverageZoneId: `${availableStore?.id}`,
      lastLat: `${location?.lat}`,
      lastLng: `${location?.lng}`,
      lastStoreId: `${availableStore?.stores?.[0]?.id ?? storeId}`,
      newAddress: newLocation.name,
      newCoverageZoneId: `${newCoverageId}`,
      newLat: `${Number(newLocation.lat).toFixed(12)}`,
      newLng: `${Number(newLocation.lng).toFixed(12)}`,
      newStoreId: `${newStoreId}`,
    });
    void Analytics.AddressInCoverage({
      coverageZoneId: `${newCoverageId}`,
      storeId: `${newStoreId}`,
    });
  };

  const applyCoverageOption = (option: ICoverageOption): void => {
    const zone = option.zone;
    const newStore = zone?.stores[0];

    if (!zone || !newStore) return;

    setIsLoading(true);
    setSuccesChange(true);

    if (availableStore?.id) {
      sendCoverageChangeEvent(option, newStore.id, zone.id);
    }

    commitStoreSelection({
      zone,
      location: {
        address: option.name,
        lat: Number(newStore.latitude),
        lng: Number(newStore.longitude),
        test: true,
        city: zone.city.toLowerCase(),
        isPickUp: false,
      },
    });

    if (showLocationModal) {
      dispatch(
        popupsRemunderAction.setPopUpsReminder({
          showLocationModal: false,
          showAddressReminder: false,
        })
      );
    }

    setSuccesChange(false);
    handleClose();
    setIsLoading(false);
    handleCallback();
  };

  const onSubmit = async (selectAddress: ICoverageOption): Promise<void> => {
    const newStore = selectAddress?.zone?.stores[0];

    if (!newStore || isLoadingZones) {
      return;
    }

    try {
      setIsLoading(true);
      void pageView.eventFindLocation();

      if (totalCountCart <= 0 || newStore.id === storeId) {
        applyCoverageOption(selectAddress);
        return;
      }

      // Shared cache key with ModalItemsOutOfStock
      const productsId = items.map((item) => String(item.product.sku)).join(",");
      const cartAvailables = await queryClient.fetchQuery({
        queryKey: [CheckoutEnum.getCartAvailableProducts, newStore.id, JSON.stringify(items)],
        queryFn: async () => await getCartAvailableProducts(newStore.id, productsId),
        staleTime: 1000 * 60 * 5,
      });

      if (
        cartAvailables &&
        (cartAvailables.payload.length <= 0 || cartAvailables.payload.some((item) => !item.is_available))
      ) {
        pendingOptionRef.current = selectAddress;
        setIsLoading(false);
        setOpenModalItemsOutStock(true);
        return;
      }

      applyCoverageOption(selectAddress);
    } catch (error) {
      setIsLoading(false);
      await Analytics.OutOfCoverage({
        lat: selectAddress.lat,
        lng: selectAddress.lng,
        address: selectAddress.name,
      });
    } finally {
      setShowCityFeedbackForm(false);
    }
  };

  function handleCityFeedbackForm(open: boolean) {
    setShowCityFeedbackForm(open);
  }

  function handleClose() {
    toggleAddressModal(false);

    if (showCityFeedbackForm) {
      setShowCityFeedbackForm(false);
    }
  }

  const handleOnSubmitItemOutStock = () => {
    if (pendingOptionRef.current) {
      applyCoverageOption(pendingOptionRef.current);
      pendingOptionRef.current = undefined;
    }
    setOpenModalItemsOutStock(false);
  };

  const handleOpenForm = () => {
    setShowCityFeedbackForm(true);
  };

  const renderOptions = () => {
    if (isLoadingZones) {
      return (
        <div tw="grid gap-s" aria-busy="true" aria-label="Cargando ubicaciones">
          {[1, 2].map((item) => (
            <Skeleton key={item} type="text" height="2.75rem" animate borderRadius="0.5rem" />
          ))}
        </div>
      );
    }

    if (isError && listStores.every((item) => !item.zone)) {
      return (
        <div tw="grid gap-s justify-items-center text-center">
          <NotificationAlert
            title="No pudimos cargar las ubicaciones disponibles"
            type={ETypeNotification.ERROR}
            variant="default"
          />
          <Button color="secondary" sizeVariant="md" variant="outlined" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      );
    }

    return (
      <AddressSelectionCard
        setSelectAddress={onSubmit}
        listStores={listStores}
        location={location}
        isDefaultSelected={showLocationModal}
        isOptionSelected={isOptionSelected}
        onSelectNoAvailable={(city: CityType) => {
          handleCityFeedbackForm(true);
          setCityFeedbackForm(city?.name);
        }}
      />
    );
  };

  return (
    <>
      <Modal
        showCenter
        isOpen={isOpen}
        handleClose={handleClose}
        closeOutSise={!showLocationModal}
        classes={{
          card: tw`p-0 w-[92%] max-w-[32rem] sm:max-w-max`,
          backdrop: tw`z-[52]`,
        }}
      >
        <StyledWrapperAddresForm>
          <StyledWrapperHeader>
            <div tw="flex items-center gap-s">
              <IconOtra tw=" w-[2.5rem] h-[3rem]" />

              <Heading type="h5" color={theme`colors.secondary.100`}>
                Elige tu ubicación
              </Heading>
            </div>
            {!showLocationModal && (
              <IconClose tw="h-6 w-6 cursor-pointer fill-secondary-100" onClick={handleClose} />
            )}
          </StyledWrapperHeader>

          <StyledSectionSelect>
            <Paragraph sizeVariant="md">
              Elige la ciudad en la que te encuentras para ver los productos disponibles y costos.
            </Paragraph>

            <NotificationAlert
              title="Debes elegir una ciudad para poder realizar tus compras"
              type={ETypeNotification.WARNING}
              variant="default"
            />
            {renderOptions()}
          </StyledSectionSelect>

          {!showCityFeedbackForm && (
            <div tw="px-m flex justify-center w-full mb-m">
              <Button
                color="secondary"
                sizeVariant="md"
                variant="text"
                type="submit"
                onClick={handleOpenForm}
                fullWidth
              >
                ¿Te encuentras en otra ciudad?
              </Button>
            </div>
          )}

          {showCityFeedbackForm && (
            <CityFeedbackForm
              cityDefault={cityDefault?.name}
              cityFormDefault={cityFeedbackForm}
              onAccept={() => {
                handleClose();
                if (cityDefault?.zone) {
                  void onSubmit(cityDefault);
                }
              }}
            />
          )}

          {isLoading && (
            <StyledAddresFormLoader>
              {!succesChange && <StyledLoaderLoading />}
              {succesChange && <StyledLoaderCheked />}
            </StyledAddresFormLoader>
          )}
        </StyledWrapperAddresForm>
      </Modal>

      {openModalItemsOutStock && (
        <ModalItemsOutOfStock
          isOpen={openModalItemsOutStock}
          handleClose={() => setOpenModalItemsOutStock(false)}
          storeId={pendingOptionRef.current?.zone?.stores[0]?.id}
          onSubmit={handleOnSubmitItemOutStock}
        />
      )}
    </>
  );
};
export default ModalAddressForm;
